import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { RootState } from "../redux/store";
import { initialState } from "./menuInitialState";
import { 
  IDishGroup, 
  IDishPutRequest, 
  IDishDeleteRequest, 
  ICategoryPutRequest, 
  ICategoryDeleteRequest 
} from "./menuTypes";
import { 
  DISH_PUT, 
  DISH_DELETE, 
  CATEGORY_PUT, 
  CATEGORY_DELETE, 
  MENU_LIST 
} from "./menuActions";
import { HttpRequest } from "../base/HttpModels";
import { BaseRequest } from "../base/BaseRequest";
import axios from "axios";
import { REQUEST_URL } from "../base/Constants";
import { logMessage } from "../utils/logger";
import { Logout } from "../login/loginSlice";

export const putDish = createAsyncThunk(
  DISH_PUT,
  async (request: HttpRequest<IDishPutRequest>, dispatch) => {
    try {
      const response = await axios.post(REQUEST_URL, request);
      logMessage("[putDish] " + response);
      const menuRequest: HttpRequest<BaseRequest> = {
        action: MENU_LIST,
        parameters: {
          user_token: request.parameters.user_token,
        },
      };
      dispatch.dispatch(getMenuList(menuRequest));
    } catch (error) {
      logMessage("[putDish] " + error);
      return null;
    }
  }
);

export const deleteDish = createAsyncThunk(
  DISH_DELETE,
  async (request: HttpRequest<IDishDeleteRequest>, dispatch) => {
    try {
      const response = await axios.post(REQUEST_URL, request);
      logMessage("[deleteDish] " + response);
      const menuRequest: HttpRequest<BaseRequest> = {
        action: MENU_LIST,
        parameters: {
          user_token: request.parameters.user_token,
        },
      };
      dispatch.dispatch(getMenuList(menuRequest));
    } catch (error) {
      logMessage("[deleteDish] " + error);
      return null;
    }
  }
);

export const putCategory = createAsyncThunk(
  CATEGORY_PUT,
  async (request: HttpRequest<ICategoryPutRequest>, dispatch) => {
    try {
      const response = await axios.post(REQUEST_URL, request);
      logMessage("[putCategory] " + response);
      const menuRequest: HttpRequest<BaseRequest> = {
        action: MENU_LIST,
        parameters: {
          user_token: request.parameters.user_token,
        },
      };
      dispatch.dispatch(getMenuList(menuRequest));
    } catch (error) {
      logMessage("[putCategory] " + error);
      return null;
    }
  }
);

export const deleteCategory = createAsyncThunk(
  CATEGORY_DELETE,
  async (request: HttpRequest<ICategoryDeleteRequest>, dispatch) => {
    try {
      const response = await axios.post(REQUEST_URL, request);
      logMessage("[deleteCategory] " + response);
      const menuRequest: HttpRequest<BaseRequest> = {
        action: MENU_LIST,
        parameters: {
          user_token: request.parameters.user_token,
        },
      };
      dispatch.dispatch(getMenuList(menuRequest));
    } catch (error) {
      logMessage("[deleteCategory] " + error);
      return null;
    }
  }
);

export const getMenuList = createAsyncThunk(
  MENU_LIST,
  async (request: HttpRequest<BaseRequest>, dispatch) => {
    try {
      const response = await axios.post(REQUEST_URL, request);
      logMessage(">>> [getMenuList] " + JSON.stringify(response));
      if(response.data.errors.length > 0 && response.data.errors[0] === "invalid_role") {
        console.log("Invalid role, logging out");
        dispatch.dispatch(Logout())
        localStorage.removeItem("persist:root-2");
        return null;
      }
      return response.data.results as Array<IDishGroup>;
    } catch (error) {
      return null;
    }
  }
);

export const menuSlice = createSlice({
  name: "menu",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(putDish.pending, (state, action) => {
        state.error = undefined;
        state.loading = true;
      })
      .addCase(putDish.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload === null) {
          state.error = "Error while updating dish";
        }
      })
      .addCase(deleteDish.pending, (state, action) => {
        state.error = undefined;
        state.loading = true;
      })
      .addCase(deleteDish.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload === null) {
          state.error = "Error while deleting dish";
        }
      })
      .addCase(putCategory.pending, (state, action) => {
        state.error = undefined;
        state.loading = true;
      })
      .addCase(putCategory.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload === null) {
          state.error = "Error while updating category";
        }
      })
      .addCase(deleteCategory.pending, (state, action) => {
        state.error = undefined;
        state.loading = true;
      })
      .addCase(deleteCategory.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload === null) {
          state.error = "Error while deleting category";
        }
      })
      .addCase(getMenuList.pending, (state, action) => {
        state.error = undefined;
        state.loading = true;
      })
      .addCase(getMenuList.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload === null) {
          state.error = "Error while retrieving menu";
        } else {
          state.menu = action.payload;
        }
      })
      .addCase(getMenuList.rejected, (state, action) => {
        state.loading = false;
        state.error = "Menu request rejected";
      });
  },
});

export const selectMenu = (state: RootState) => state.menuState;

export default menuSlice.reducer;
